import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  buildBankSelectOptionsFromAccounts,
  buildPaymentBeneficiaryOptions,
  SAUDI_BANK_CODES_PRIMARY,
  SAUDI_CHECK_AMOUNT_TIERS,
  validateSaudiCheckNumber,
  isPostDatedCheck,
} from './bankingSaudiHelpers';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileCheck,
  Plus,
  Calendar,
  Filter,
  Download,
  Upload,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  X,
  LayoutGrid,
  Layers,
  Building2,
  DollarSign,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import localApiAdapter from '../../core/services/localApiAdapter';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { resolveBanqueEntityReference } from './bankingHelpers';
import { useBanqueConfig, sliceBanqueListItems } from './useBanqueConfig';
import { mapCheckFromApi, mapCheckToApi } from './bankingApiMappers';
import { useSettings } from '../../core/contexts/SettingsContext';
import { exportBankingRows, exportBankingPdf, importBankingRows } from './bankingExportImport';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { useClients } from '../../core/contexts/ClientsContext';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';

registerLocale('ar', ar);

const PendingChecksPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.pendingChecksPage.${key}`, opts);
  const fmt = (value) => formatBrandMoney(value);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { settings } = useSettings();
  const bankingConfig = useBanqueConfig();
  const { clients } = useClients();
  const entityLabel = p('entity_label', { defaultValue: 'الشيك' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'شيكات' });

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Advanced options
  const [mainViewMode, setMainViewMode] = useState('all_checks');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [columnVisibility, setColumnVisibility] = useState({
    numeroCheque: true,
    date: true,
    emetteur: true,
    banque: true,
    montant: true,
    dateEcheance: true,
  });

  // Data
  const [checks, setChecks] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    numeroCheque: '',
    emetteur: '',
    banque: '',
    montant: '',
    dateEcheance: '',
    status: '',
    account_id: '',
    notes: ''
  });
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadBankAccounts();
    loadSuppliers();
    loadChecks();
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/fournisseurs');
      const raw = Array.isArray(response) ? response : response?.data || [];
      setSuppliers(raw);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
    }
  };

  useEffect(() => {
    if (!showModal || editingRecord || !bankAccounts.length) return;
    const acct = bankAccounts[0];
    setFormData((prev) => {
      if (prev.account_id) return prev;
      return {
        ...prev,
        account_id: String(acct.id),
        banque: acct.bank_name || prev.banque,
        status: prev.status || 'pending',
        date: prev.date || new Date().toISOString().slice(0, 10),
        dateEcheance: prev.dateEcheance || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      };
    });
  }, [showModal, editingRecord, bankAccounts]);

  // ===== HELPER FUNCTIONS =====
  const issuerOptions = useMemo(
    () => buildPaymentBeneficiaryOptions({
      clients,
      suppliers,
      orders: checks.map((c) => ({ beneficiaire: c.emetteur })),
    }),
    [clients, suppliers, checks],
  );

  const checkBankOptions = useMemo(
    () => buildBankSelectOptionsFromAccounts(bankAccounts, checks.map((c) => c.banque)),
    [bankAccounts, checks],
  );

  const columnStatusFilterOptions = [
    { value: 'pending', label: p('status_pending') },
    { value: 'cleared', label: p('status_cleared') },
    { value: 'bounced', label: p('status_bounced') },
  ];

  const loadBankAccounts = async () => {
    try {
      const accounts = await localApiAdapter.get('/banking/accounts');
      setBankAccounts(normalizeAndSyncBankAccounts(accounts));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const loadChecks = async () => {
    setLoading(true);
    try {
      const data = await localApiAdapter.get('/banking/checks');
      setChecks(sortRecordsNewestFirst((Array.isArray(data) ? data : []).map(mapCheckFromApi)));
    } catch (error) {
      console.error('Error loading checks:', error);
      notifyError(showNotify, error.message || p('load_error'));
      setChecks([]);
    } finally {
      setLoading(false);
    }
  };

  const checkExportHeaders = [
    { key: 'numeroCheque', label: p('col_code') },
    { key: 'date', label: p('col_date') },
    { key: 'emetteur', label: p('col_issuer') },
    { key: 'banque', label: p('col_bank') },
    { key: 'montant', label: p('col_amount') },
    { key: 'dateEcheance', label: p('col_due_date') },
    { key: 'status', label: p('col_status') },
  ];

  const handleExportCSV = () => { exportBankingRows(filteredChecks, checkExportHeaders, 'cheques', 'csv'); setShowExportDropdown(false); };
  const handleExportExcel = () => { exportBankingRows(filteredChecks, checkExportHeaders, 'cheques', 'excel'); setShowExportDropdown(false); };
  const handleExportPDF = () => { exportBankingPdf(p('title')); setShowExportDropdown(false); };
  const handleImport = () => {
    importBankingRows({
      headers: checkExportHeaders,
      schema: { requiredFields: ['numeroCheque', 'emetteur', 'montant'], validators: { montant: (v) => !v || !Number.isNaN(parseFloat(v)) } },
      mapRow: (row) => mapCheckToApi({
        numeroCheque: row.numeroCheque,
        date: row.date || new Date().toISOString().slice(0, 10),
        emetteur: row.emetteur,
        banque: row.banque,
        montant: row.montant,
        dateEcheance: row.dateEcheance,
        status: row.status || 'pending',
        account_id: bankAccounts[0]?.id,
      }),
      postRow: (payload) => localApiAdapter.post('/banking/checks', payload),
      onComplete: loadChecks,
    });
    setShowImportDropdown(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredCheckIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const generateCheckNumber = useCallback(
    () => resolveBanqueEntityReference(settings, 'bankCheck', 'CHQ', checks, (c) => c.numeroCheque),
    [settings, checks],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const numeroCheque = resolveBanqueEntityReference(
        settings,
        'bankCheck',
        'CHQ',
        checks,
        (c) => c.numeroCheque,
        formData.numeroCheque,
      );
      if (numeroCheque && !validateSaudiCheckNumber(numeroCheque)) {
        notifyError(showNotify, p('check_number_invalid'));
        return;
      }
      const payload = mapCheckToApi({
        ...formData,
        numeroCheque,
        account_id: formData.account_id || bankAccounts[0]?.id,
      });
      if (!payload.account_id) {
        notifyError(showNotify, p('account_required'));
        return;
      }
      if (!formData.montant || Number.isNaN(parseFloat(formData.montant)) || parseFloat(formData.montant) <= 0) {
        notifyError(showNotify, p('amount_required', { defaultValue: 'المبلغ مطلوب' }));
        return;
      }
      if (editingRecord) {
        await localApiAdapter.put(`/banking/checks/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/banking/checks', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadChecks();
      resetForm();
    } catch (error) {
      console.error('Error saving check:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const handleEdit = (check) => {
    setEditingRecord(check);
    setFormData({
      ...check,
      montant: check.montant ?? '',
      account_id: check.account_id || '',
      notes: check.notes || '',
    });
    setShowModal(true);
  };

  const handleView = (check) => {
    setViewingRecord(check);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`/banking/checks/${id}`);
      notifyDeleted(showNotify, entityLabel);
      await loadChecks();
    } catch (error) {
      console.error('Error deleting check:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) return;
    try {
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/banking/checks/${id}`)));
      const deleteCount = selectedRecords.length;
      setSelectedRecords([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadChecks();
    } catch (error) {
      console.error('Error bulk deleting checks:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const openNewCheckModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    const due = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const acct = bankAccounts[0];
    setEditingRecord(null);
    setFormData({
      date: today,
      numeroCheque: generateCheckNumber(),
      emetteur: '',
      banque: acct?.bank_name || '',
      montant: '',
      dateEcheance: due,
      status: 'pending',
      account_id: acct ? String(acct.id) : '',
      notes: '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ date: '', numeroCheque: '', emetteur: '', banque: '', montant: '', dateEcheance: '', status: '', account_id: '', notes: '' });
    setEditingRecord(null);
    setShowModal(false);
  };

  const checkStatusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'cleared', label: p('status_cleared'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'bounced', label: p('status_bounced'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const apiStatus = newStatus === 'pending' ? 'issued' : newStatus;
      await localApiAdapter.patch(`/banking/checks/${recordId}/status`, { status: apiStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadChecks();
    } catch (error) {
      console.error('Error updating check status:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const renderCheckStatusBadge = (status, recordId, { clickable = true } = {}) => (
    <QawaemClickableStatusBadge
      status={status || 'pending'}
      recordId={recordId}
      options={checkStatusOptions}
      onChange={clickable ? handleStatusChange : undefined}
      title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
    />
  );

  // Calculate stats
  const stats = {
    total: checks.length,
    pending: checks.filter(c => c.status === 'pending').length,
    cleared: checks.filter(c => c.status === 'cleared').length,
    bounced: checks.filter(c => c.status === 'bounced').length,
    totalAmount: checks.reduce((sum, c) => sum + c.montant, 0)
  };

  const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(dateLocale);
  };

  // Main tabs
  const mainTabs = [
    { id: 'all_checks', nameKey: 'tab_general', icon: LayoutGrid },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: CheckCircle },
    { id: 'par_montant', nameKey: 'tab_by_amount', icon: DollarSign },
    { id: 'par_banque', nameKey: 'tab_by_bank', icon: Building2 }
  ];

  // Quick filter buttons
  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'pending', nameKey: 'filter_pending', icon: Clock },
    { id: 'cleared', nameKey: 'filter_cleared', icon: CheckCircle },
    { id: 'bounced', nameKey: 'filter_bounced', icon: AlertCircle }
  ];

  // Distribution data
  const checkDistribution = {
    all_checks: {
      titleKey: 'dist_overview',
      data: [
        { nameKey: 'dist_pending', filterKey: 'pending', count: stats.pending, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_cleared', filterKey: 'cleared', count: stats.cleared, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_bounced', filterKey: 'bounced', count: stats.bounced, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { nameKey: 'dist_total', filterKey: 'total', count: stats.total, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    par_statut: {
      titleKey: 'dist_by_status',
      data: [
        { nameKey: 'dist_pending', filterKey: 'pending', count: stats.pending, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_cleared', filterKey: 'cleared', count: stats.cleared, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_bounced', filterKey: 'bounced', count: stats.bounced, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_montant: {
      titleKey: 'dist_by_amount',
      data: [
        { nameKey: 'dist_amount_over_1m', filterKey: 'over_1m', count: checks.filter(c => c.montant >= SAUDI_CHECK_AMOUNT_TIERS.over100k).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { nameKey: 'dist_amount_500k_1m', filterKey: '500k_1m', count: checks.filter(c => c.montant >= SAUDI_CHECK_AMOUNT_TIERS.over50k && c.montant < SAUDI_CHECK_AMOUNT_TIERS.over100k).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_amount_under_500k', filterKey: 'under_500k', count: checks.filter(c => c.montant < SAUDI_CHECK_AMOUNT_TIERS.over50k).length, color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    },
    par_banque: {
      titleKey: 'dist_by_bank',
      data: [
        ...SAUDI_BANK_CODES_PRIMARY.map((code, idx) => ({
          name: code,
          filterKey: code,
          count: checks.filter(c => c.banque === code).length,
          color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-purple-500 to-purple-600'][idx],
        })),
        { nameKey: 'dist_bank_other', filterKey: 'other', count: checks.filter(c => !SAUDI_BANK_CODES_PRIMARY.includes(c.banque)).length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    }
  };

  const distributionInfo = checkDistribution[mainViewMode] || checkDistribution.all_checks;

  const columnFilterRules = [
    textColumnRule('numeroCheque', (row) => row.numeroCheque),
    textColumnRule('date', (row) => (row.date ? new Date(row.date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('emetteur', (row) => row.emetteur),
    textColumnRule('montant', (row) => String(row.montant ?? '')),
    textColumnRule('dateEcheance', (row) => (row.dateEcheance ? new Date(row.dateEcheance).toLocaleDateString(dateLocale) : '')),
    exactColumnRule('status', (row) => row.status),
  ];

  // Filter checks
  const baseFilteredChecks = checks.filter(check => {
    if (viewMode === 'cards' && searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !check.numeroCheque?.toLowerCase().includes(term) &&
        !check.emetteur?.toLowerCase().includes(term) &&
        !check.banque?.toLowerCase().includes(term)
      ) return false;
    }
    
    if (mainViewMode === 'all_checks') {
      if (quickFilter !== 'all' && check.status !== quickFilter) return false;
    }
    
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_checks':
        case 'par_statut':
          if (activeDistributionFilter !== 'total' && check.status !== activeDistributionFilter) return false;
          break;
        case 'par_montant':
          if (activeDistributionFilter === 'over_1m' && check.montant < SAUDI_CHECK_AMOUNT_TIERS.over100k) return false;
          if (activeDistributionFilter === '500k_1m' && (check.montant < SAUDI_CHECK_AMOUNT_TIERS.over50k || check.montant >= SAUDI_CHECK_AMOUNT_TIERS.over100k)) return false;
          if (activeDistributionFilter === 'under_500k' && check.montant >= SAUDI_CHECK_AMOUNT_TIERS.over50k) return false;
          break;
        case 'par_banque':
          if (activeDistributionFilter === 'other') {
            if (SAUDI_BANK_CODES_PRIMARY.includes(check.banque)) return false;
          } else if (check.banque !== activeDistributionFilter) {
            return false;
          }
          break;
        default:
          break;
      }
    }
    
    return true;
  });

  const filteredChecks = applyColumnFilters(baseFilteredChecks, columnFilters, columnFilterRules)
    .sort((a, b) => b.id - a.id);

  const pagedChecks = useMemo(
    () => sliceBanqueListItems(filteredChecks, bankingConfig.itemsPerPage),
    [filteredChecks, bankingConfig.itemsPerPage],
  );

  const filteredCheckIds = useMemo(() => filteredChecks.map((c) => c.id), [filteredChecks]);
  const allRowsSelected = filteredCheckIds.length > 0 && filteredCheckIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total_checks'), value: stats.total, icon: FileCheck, variant: 'copper' },
    { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
    { key: 'cleared', label: p('stat_cleared'), value: stats.cleared, icon: CheckCircle, variant: 'copper' },
    { key: 'amount', label: p('stat_total_amount'), value: fmt(stats.totalAmount), icon: DollarSign, variant: 'forest' },
  ], 4), [stats, p, fmt]);

  const qawaemColumnDefs = useMemo(() => [
    { key: 'numeroCheque', label: p('col_code'), checked: columnVisibility.numeroCheque !== false, required: true },
    { key: 'date', label: p('col_date'), checked: columnVisibility.date !== false },
    { key: 'emetteur', label: p('col_issuer'), checked: columnVisibility.emetteur !== false },
    { key: 'banque', label: p('col_bank'), checked: columnVisibility.banque !== false },
    { key: 'montant', label: p('col_amount'), checked: columnVisibility.montant !== false },
    { key: 'dateEcheance', label: p('col_due_date'), checked: columnVisibility.dateEcheance !== false },
    { key: 'actions', label: p('col_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['numeroCheque', 'date', 'emetteur', 'banque', 'montant', 'dateEcheance'].filter((k) => isColVisible(k)).length + 1;

  return (
    <QawaemErpListPageShell
      className="space-y-3"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={FileCheck}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={t('common.delete', { defaultValue: 'حذف' })}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedRecords.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openNewCheckModal}
      createLabel={p('new_check')}
      headerActions={(
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
        >
          <Filter className="h-5 w-5" />
          <span className="hidden sm:inline">{p('advanced_options')}</span>
          {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import'),
        exportLabel: p('export'),
        onImportCSV: handleImport,
        onImportExcel: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >

      {/* Advanced Options */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_checks' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => {
                const itemKey = item.filterKey || item.nameKey;
                const itemLabel = item.nameKey ? p(item.nameKey) : item.name;
                return (
                <button key={itemKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === itemKey ? null : itemKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === itemKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === itemKey ? 'text-white' : 'text-white/80'}`}>{itemLabel}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === itemKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              );})}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Table */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('numeroCheque') ? (
                    <TableColumnHeaderFilter columnKey="numeroCheque" label={p('col_code')} value={columnFilters.numeroCheque || ''} onChange={(v) => setFilter('numeroCheque', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('emetteur') ? (
                    <TableColumnHeaderFilter columnKey="emetteur" label={p('col_issuer')} value={columnFilters.emetteur || ''} onChange={(v) => setFilter('emetteur', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('banque') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_bank')}</th>
                  ) : null}
                  {isColVisible('montant') ? (
                    <TableColumnHeaderFilter columnKey="montant" label={p('col_amount')} value={columnFilters.montant || ''} onChange={(v) => setFilter('montant', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('dateEcheance') ? (
                    <TableColumnHeaderFilter columnKey="dateEcheance" label={p('col_due_date')} value={columnFilters.dateEcheance || ''} onChange={(v) => setFilter('dateEcheance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredChecks.length > 0 ? (
                  pagedChecks.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('numeroCheque') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.numeroCheque}</span></td>
                      ) : null}
                      {isColVisible('date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {formatDate(record.date)}
                            </div>
                            {isPostDatedCheck(record.date, record.dateEcheance) && record.status === 'pending' && (
                              <span className="text-xs text-amber-400/90">{p('status_post_dated')}</span>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {isColVisible('emetteur') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.emetteur}</span></td>
                      ) : null}
                      {isColVisible('banque') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.banque || '-'}</span></td>
                      ) : null}
                      {isColVisible('montant') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-bold text-lg">{fmt(record.montant || 0)}</span></div></td>
                      ) : null}
                      {isColVisible('dateEcheance') ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 text-white/70 text-sm">
                            <Clock size={14} className="text-white/50" />
                            {formatDate(record.dateEcheance)}
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex items-center justify-center gap-2">
                          {renderCheckStatusBadge(record.status, record.id)}
                          <QawaemErpRowActions
                            onView={() => handleView(record)}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><FileCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_no_checks')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_checks_total', { count: filteredChecks.length })}</span>
                      <span>{p('footer_total_amount')} <span className="text-white font-semibold">{fmt(filteredChecks.reduce((sum, c) => sum + (parseFloat(c.montant) || 0), 0))}</span></span>
                      <span>{p('footer_pending')} <span className="text-amber-400 font-semibold">{filteredChecks.filter(c => c.status === 'pending').length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredChecks.length > 0 ? (
              pagedChecks.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><FileCheck size={20} className="text-emerald-400" />{record.numeroCheque}</h3>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-white/70 text-sm">
                          <Clock size={14} className="text-white/50" />
                          {formatDate(record.date)}
                        </div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('label_issuer')}:</span> {record.emetteur}</div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('label_bank')}:</span> {record.banque}</div>
                        <div className="text-white font-bold text-lg">{fmt(record.montant || 0)}</div>
                        <div className="flex items-center gap-1.5 text-white/70 text-sm">
                          <Clock size={14} className="text-white/50" />
                          {p('label_due_date')}: {formatDate(record.dateEcheance)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      {renderCheckStatusBadge(record.status, record.id)}
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><FileCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_no_checks')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && viewingRecord ? (
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">{p('modal_view_title')}</h2>
                  <button type="button" onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_code')}</p><p className="text-white font-medium font-mono">{viewingRecord.numeroCheque}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{formatDate(viewingRecord.date)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_status')}</p>{renderCheckStatusBadge(viewingRecord.status, viewingRecord.id, { clickable: false })}</div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_issuer')}</p><p className="text-white font-medium">{viewingRecord.emetteur || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_bank')}</p><p className="text-white font-medium">{viewingRecord.banque || '-'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_due_date_full')}</p><p className="text-white font-medium">{formatDate(viewingRecord.dateEcheance)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_amount')}</p><p className="text-emerald-400 font-bold text-xl">{fmt(viewingRecord.montant || 0)}</p></div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      ) : null}

      {/* Add/Edit Modal — tabs (Products.js pattern) */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('modal_edit_title') : p('modal_new_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="check-form" data-testid="bank-check-submit" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="check-form" data-testid="bank-check-form" noValidate onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code')} <span className="qawaem-required-mark">*</span></label><input type="text" data-testid="bank-check-number" required value={formData.numeroCheque} onChange={(e) => setFormData({ ...formData, numeroCheque: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('placeholder_auto_generated')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label><input type="date" required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_due_date_full')} <span className="qawaem-required-mark">*</span></label><input type="date" required value={formData.dateEcheance} onChange={(e) => setFormData({ ...formData, dateEcheance: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_issuer')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-check-issuer" value={formData.emetteur} onChange={(value) => setFormData({ ...formData, emetteur: value })} options={issuerOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_select_or_type')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_account')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-check-account" value={String(formData.account_id || '')} onChange={(value) => { const acct = bankAccounts.find((a) => String(a.id) === String(value)); setFormData({ ...formData, account_id: value, banque: acct?.bank_name || formData.banque }); }} options={bankAccounts.map((a) => ({ value: String(a.id), label: `${a.bank_name} — ${a.account_name}`, isDefault: true }))} searchable={true} creatable={false} autoSelect={false} placeholder={p('placeholder_select_or_type')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_bank')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-check-bank" value={formData.banque} onChange={(value) => setFormData({ ...formData, banque: value })} options={checkBankOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_select_or_type')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_amount', { defaultValue: p('placeholder_amount_sar') })} <span className="qawaem-required-mark">*</span></label><CustomNumberInput data-testid="bank-check-amount" step={0.01} value={formData.montant || ''} onChange={(value) => setFormData({ ...formData, montant: value })} placeholder="0.00" /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="bank-check-status" value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={[
                    { value: 'pending', label: p('status_pending'), icon: <Clock size={14} className="text-amber-400" />, isDefault: true },
                    { value: 'cleared', label: p('status_cleared'), icon: <CheckCircle size={14} className="text-green-400" />, isDefault: true },
                    { value: 'bounced', label: p('status_bounced'), icon: <XCircle size={14} className="text-red-400" />, isDefault: true }
                  ]} searchable={false} autoSelect={false} /></div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default PendingChecksPage;


